import type { AcmMetricKey, AcmRecord } from '../types/kpi.js';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business グラフ表示の粒度選択肢（日次/週次/月次/四半期/年次）。
 * @graph-connects none
 */
export type Granularity = 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly';

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 粒度ラベルの日本語表示。
 * @graph-connects none
 */
export const GRANULARITY_LABELS: Record<Granularity, string> = {
  daily: '日次',
  weekly: '週次',
  monthly: '月次',
  quarterly: '四半期',
  yearly: '年次',
};

/** @graph-connects none */
export const GRANULARITY_OPTIONS: readonly { key: Granularity; label: string }[] = [
  { key: 'daily', label: '日次' },
  { key: 'weekly', label: '週次' },
  { key: 'monthly', label: '月次' },
  { key: 'quarterly', label: '四半期' },
  { key: 'yearly', label: '年次' },
];

/**
 * 集約方式。snapshot はストック指標（期末の値を採用）、sum はフロー指標（期間合計）。
 * @graph-connects none
 */
type AggregationType = 'snapshot' | 'sum';

/** @graph-connects none */
const METRIC_AGGREGATION: Record<AcmMetricKey, AggregationType> = {
  accountCount: 'snapshot',
  accountNew: 'sum',
  subscriptionMemberCount: 'snapshot',
  serviceStartedCount: 'snapshot',
  serviceNotStartedCount: 'snapshot',
  subscriptionNew: 'sum',
  subscriptionServiceStartNew: 'sum',
  subscriptionCancelled: 'sum',
  stylingOpening: 'snapshot',
  stylingNew: 'sum',
  stylingCompleted: 'sum',
  stylingCancelled: 'sum',
  stylingClosing: 'snapshot',
  ticketOpening: 'snapshot',
  ticketGranted: 'sum',
  ticketUsed: 'sum',
  ticketExpired: 'sum',
  ticketClosing: 'snapshot',
  returnCount: 'sum',
  sessionCount: 'sum',
  organicSessionCount: 'sum',
  organicGa4Sessions: 'sum',
  organicGa4SubscribeComplete: 'sum',
  semGa4Sessions: 'sum',
  semGa4SubscribeComplete: 'sum',
};

/** @graph-connects none */
export function isWeekend(dateStr: string): boolean {
  const [y, m, d] = dateStr.split('-').map(Number);
  const day = new Date(y, m - 1, d).getDay();
  return day === 0 || day === 6;
}

/** @graph-connects none */
export function periodKey(date: string, granularity: Exclude<Granularity, 'daily'>): string {
  const [y, m, d] = date.split('-').map(Number);

  if (granularity === 'weekly') {
    const dt = new Date(y, m - 1, d);
    const day = dt.getDay();
    const monday = new Date(dt);
    monday.setDate(dt.getDate() - ((day + 6) % 7));
    const wy = String(monday.getFullYear());
    const wm = String(monday.getMonth() + 1).padStart(2, '0');
    const wd = String(monday.getDate()).padStart(2, '0');
    return `${wy}-${wm}-${wd}`;
  }

  if (granularity === 'monthly') return `${String(y)}-${String(m).padStart(2, '0')}`;

  if (granularity === 'quarterly') {
    const q = String(Math.ceil(m / 3));
    return `${String(y)}-Q${q}`;
  }

  return String(y);
}

/** @graph-connects none */
function sumMetric(group: AcmRecord[], mk: AcmMetricKey): number {
  let total = 0;
  for (const r of group) {
    total += r[mk];
  }
  return total;
}

/** @graph-connects none */
function aggregateBucket(group: AcmRecord[]): AcmRecord {
  const last = group[group.length - 1];
  const aggregated = { date: last.date } as AcmRecord;
  const metricKeys = Object.keys(METRIC_AGGREGATION) as AcmMetricKey[];
  for (const mk of metricKeys) {
    aggregated[mk] = METRIC_AGGREGATION[mk] === 'snapshot' ? last[mk] : sumMetric(group, mk);
  }
  return aggregated;
}

/**
 * @graph-stack ac-mens-hub
 * @graph-domain Engineering
 * @graph-business 日次 AcmRecord 配列を指定粒度で集約する。ストック指標は期末値、フロー指標は合計。
 * @graph-connects none
 */
export function aggregateRecords(
  rows: AcmRecord[],
  granularity: Granularity,
): { label: string; record: AcmRecord }[] {
  if (granularity === 'daily') {
    return rows.map((r) => ({ label: r.date, record: r }));
  }

  const sorted = [...rows].sort((a, b) => (a.date < b.date ? -1 : 1));

  const buckets = new Map<string, AcmRecord[]>();
  for (const row of sorted) {
    const key = periodKey(row.date, granularity);
    const arr = buckets.get(key);
    if (arr) {
      arr.push(row);
    } else {
      buckets.set(key, [row]);
    }
  }

  const result: { label: string; record: AcmRecord }[] = [];
  for (const [key, group] of buckets) {
    const aggregated = aggregateBucket(group);
    result.push({ label: key, record: aggregated });
  }

  return result;
}
